// The Live screen's derivations, pure: the stored history folded into
// per-turn blocks for the conversation, and an account's line read as
// the card the workers list shows. The screen renders these; nothing
// here touches a socket or the DOM.
import { HistoryKind } from "@/gen/psypen/v1/session_pb";
import type { StoredChild, StoredHistoryEvent, StoredLine } from "@/session/types";

// One turn's block in the conversation: the events of one utterance in
// the order they were stored, under the time of its first event.
export type TurnBlock = {
  turn: string;
  at: string | null;
  events: StoredHistoryEvent[];
};

// The live-only kinds never reach the store (they are streamed, not
// stored), but a skip costs nothing if one ever arrives.
function shown(ev: StoredHistoryEvent): boolean {
  return ev.kind !== HistoryKind.UNSPECIFIED && ev.kind !== HistoryKind.ACTING && ev.kind !== HistoryKind.HEARD;
}

// groupTurns folds the stored history, oldest first, into blocks: one
// per turn, consecutive events of the same turn sharing a block (the
// stream is in order, and a turn's events arrive together). An event
// with no turn id stands as its own block.
export function groupTurns(history: StoredHistoryEvent[]): TurnBlock[] {
  const blocks: TurnBlock[] = [];
  for (const ev of history) {
    if (!shown(ev)) continue;
    const last = blocks[blocks.length - 1];
    if (last && last.turn === ev.turn && ev.turn !== "") {
      last.events.push(ev);
    } else {
      blocks.push({ turn: ev.turn, at: ev.at, events: [ev] });
    }
  }
  return blocks;
}

// One worker's card: the account's line (spec 03.2) read as what the
// Live screen shows at a glance — the goal it works on, its state, its
// latest mid-step line, and what the work handed over.
export type WorkerCard = {
  id: string;
  name: string;
  // Kind is "job" or "standing".
  kind: string;
  // State is the one word for speech and the client's colour:
  // working, needs you, sleeping, failed, idle, done.
  state: string;
  // Worker is "queued", "working", "holding", "idle", or empty.
  worker: string;
  // Goal is the first open item, else the line's detail text.
  goal: string;
  // Last is the worker's latest mid-step line while one is on the
  // account.
  last: string;
  // Need is the first need's words, when it holds for the user.
  need: string;
  // Unread is the account's unread count.
  unread: number;
  open: string[];
  doneCount: number;
  // Handed is what the work handed over: PRs and links.
  handed: StoredChild[];
};

export function workerCard(l: StoredLine): WorkerCard {
  return {
    id: l.id,
    name: l.name || l.id,
    kind: l.kind,
    state: l.description || l.worker || "idle",
    worker: l.worker,
    goal: l.open[0] ?? l.detail,
    last: l.last,
    need: l.need,
    unread: l.unread,
    open: [...l.open],
    doneCount: l.done.length,
    handed: [...l.children],
  };
}
